import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/i18n";
import { formatMoney } from "@/lib/utils";
import { proRataPaise } from "@/lib/feeSelection";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { monthLabel } from "@/components/FeePlan";

// Custom entry inside the Student Fee Payment dialog (custom-fee plan §4):
// months of a term, or a partial / item-picked one-time fee.

const toMajor = (minor) => (minor / 100).toString();
const toMinor = (text) => Math.round(parseFloat(text) * 100);

export function customRange(line, standard) {
  const min = line?.custom_min_bps == null ? null : Math.floor((standard * line.custom_min_bps) / 10000);
  const max = line?.custom_max_bps == null ? null : Math.floor((standard * line.custom_max_bps) / 10000);
  return { min, max };
}

/** Why a custom entry cannot be submitted yet (null when it can). */
export function customEntryProblem(line, standard, entry, { term = false, needsItems = false } = {}) {
  if (term && !(entry.months || []).length) return "months";
  if (needsItems && !Object.values(entry.items || {}).some((qty) => Number(qty) > 0)) return "items";
  const value = toMinor(entry.amount);
  if (!Number.isFinite(value) || value <= 0) return "amount";
  const { min, max } = customRange(line, standard);
  if ((min != null && value < min) || (max != null && value > max)) return "range";
  if (!entry.reason) return "reason";
  return null;
}

function ReasonAndNote({ entry, onChange, reasons, testId }) {
  const { t, language } = useI18n();
  const reasonLabel = (row) => (language === "ar" && row.metadata?.label_ar) || row.label;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div>
        <Label className="text-xs uppercase">{t("feePlan.reason")}<span className="ms-1 text-[var(--error)]" aria-hidden="true">*</span></Label>
        <Select value={entry.reason || ""} onValueChange={(reason) => onChange({ ...entry, reason })}>
          <SelectTrigger className="mt-1 h-9" data-testid={`${testId}-reason`}><SelectValue placeholder={t("feePlan.selectReason")} /></SelectTrigger>
          <SelectContent>
            {(reasons || []).map((row) => <SelectItem key={row.value} value={row.value}>{reasonLabel(row)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase">{t("feePlan.note")}</Label>
        <Input className="mt-1 h-9" value={entry.note || ""} maxLength={240} onChange={(e) => onChange({ ...entry, note: e.target.value })} data-testid={`${testId}-note`} />
      </div>
    </div>
  );
}

function AmountField({ line, standard, entry, onChange, suggested, testId }) {
  const { t } = useI18n();
  const { min, max } = customRange(line, standard);
  const problem = customEntryProblem(line, standard, { ...entry, reason: "x", months: ["x"], items: { x: 1 } });
  return (
    <div>
      <Label className="text-xs uppercase">{t("feePlan.amount")}</Label>
      <Input
        type="number"
        min="0"
        step="0.01"
        value={entry.amount ?? ""}
        onChange={(e) => onChange({ ...entry, amount: e.target.value, edited: true })}
        aria-invalid={entry.amount !== "" && entry.amount != null && problem != null}
        className={`mt-1 h-9 ${entry.amount !== "" && entry.amount != null && problem ? "border-[var(--error)]" : ""}`}
        data-testid={`${testId}-amount`}
      />
      <div className="text-xs text-[var(--text-muted)] mt-1">
        {suggested != null && `${t("customFee.suggested", { amount: formatMoney(suggested) })} · `}
        {t("feePlan.standardAmount", { amount: formatMoney(standard) })}
        {(min != null || max != null) && ` · ${t("feePlan.allowedRange", { min: formatMoney(min ?? 0), max: max == null ? "∞" : formatMoney(max) })}`}
      </div>
    </div>
  );
}

/** Custom months for one term of a term fee. */
export function TermCustomPanel({ line, term, months, takenMonths, entry, onChange, reasons }) {
  const { t, language } = useI18n();
  const standard = line.standard_termAmounts?.[`term${term}`] ?? line.termAmounts?.[`term${term}`] ?? 0;
  const picked = entry.months || [];
  const suggested = picked.length ? proRataPaise(standard, picked.length, months.length) : null;
  const toggle = (month) => {
    if (takenMonths.has(month)) return;
    const next = picked.includes(month) ? picked.filter((m) => m !== month) : [...picked, month].sort();
    const update = { ...entry, months: next };
    // Pro-rata suggestion until the amount is typed over.
    if (!entry.edited) update.amount = next.length ? toMajor(proRataPaise(standard, next.length, months.length)) : "";
    onChange(update);
  };
  const testId = `custom-${line.fee_head_id}-${term}`;
  return (
    <div className="mt-2 rounded border border-dashed border-[var(--accent)] p-3 space-y-2" data-testid={testId}>
      <div className="text-xs text-[var(--text-muted)]">{t("feePlan.coveredMonths")}</div>
      <div className="flex flex-wrap gap-1" data-testid={`${testId}-months`}>
        {months.map((month) => {
          const active = picked.includes(month);
          const taken = takenMonths.has(month);
          return (
            <button
              key={month}
              type="button"
              disabled={taken}
              onClick={() => toggle(month)}
              aria-pressed={active}
              title={taken ? t("feePlan.alreadyCharged") : undefined}
              className={`rounded border px-2 py-1 text-xs ${active ? "border-[var(--accent)] bg-[var(--accent)] text-white" : taken ? "border-[var(--subtle)] text-[var(--text-muted)] line-through opacity-60" : "border-[var(--subtle)]"}`}
            >
              {monthLabel(month, language)}
            </button>
          );
        })}
      </div>
      <AmountField line={line} standard={standard} entry={entry} onChange={onChange} suggested={suggested} testId={testId} />
      <ReasonAndNote entry={entry} onChange={onChange} reasons={reasons} testId={testId} />
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" className="accent-[var(--accent)]" checked={Boolean(entry.save)} onChange={(e) => onChange({ ...entry, save: e.target.checked })} data-testid={`${testId}-save`} />
        {t("customFee.saveToPlan")}
      </label>
    </div>
  );
}

/** Custom amount for a one-time fee; picks stock items when the fee is linked to inventory. */
export function OtherCustomPanel({ line, studentId, uniformSize, entry, onChange, reasons }) {
  const { t, language } = useI18n();
  const standard = line.standard_amount_paise ?? line.amount_paise ?? 0;
  const { data: items, isFetching } = useQuery({
    queryKey: ["uniform-items", studentId, line.fee_head_id, uniformSize],
    queryFn: async () => unwrap(await api.get("/invoices/uniform-items", { params: { student_id: studentId, fee_head_id: line.fee_head_id, uniform_size: uniformSize } })),
    enabled: Boolean(line.inventory_mapped && studentId && uniformSize),
  });
  const picked = entry.items || {};
  const itemsTotal = (items || []).reduce((sum, item) => sum + (Number(picked[item.item_id]) || 0) * (item.unit_price_paise || 0), 0);
  const setQty = (itemId, qty) => {
    const next = { ...picked, [itemId]: qty };
    const total = (items || []).reduce((sum, item) => sum + (Number(next[item.item_id]) || 0) * (item.unit_price_paise || 0), 0);
    const update = { ...entry, items: next };
    if (!entry.edited && total > 0) update.amount = toMajor(total);
    onChange(update);
  };
  const testId = `custom-other-${line.fee_head_id}`;
  return (
    <div className="mt-2 rounded border border-dashed border-[var(--accent)] p-3 space-y-2" data-testid={testId}>
      {line.inventory_mapped && (
        <div>
          <div className="text-xs text-[var(--text-muted)] mb-1">{t("customFee.itemsBought")}</div>
          {!uniformSize ? (
            <div className="text-xs text-[var(--text-muted)]" data-testid={`${testId}-size-first`}>{t("customFee.pickSizeFirst")}</div>
          ) : isFetching ? (
            <div className="text-xs text-[var(--text-muted)]">…</div>
          ) : !(items || []).length ? (
            <div className="text-xs text-[var(--text-muted)]">{t("customFee.noItems")}</div>
          ) : (
            <div className="space-y-1" data-testid={`${testId}-items`}>
              {items.map((item) => {
                const qty = Number(picked[item.item_id]) || 0;
                return (
                  <div key={item.item_id} className="flex items-center justify-between gap-2 text-sm">
                    <label className="flex items-center gap-2 min-w-0">
                      <input type="checkbox" className="accent-[var(--accent)]" checked={qty > 0} onChange={(e) => setQty(item.item_id, e.target.checked ? 1 : 0)} data-testid={`${testId}-item-${item.sku}`} />
                      <span className="truncate">{(language === "ar" && item.name_ar) || item.name}</span>
                    </label>
                    <span className="flex items-center gap-2 shrink-0">
                      <Input type="number" min="0" max={Math.max(item.default_qty, 1)} value={qty || ""} onChange={(e) => setQty(item.item_id, Math.max(0, parseInt(e.target.value || "0", 10)))} className="h-8 w-16" aria-label={t("customFee.qty")} />
                      <span className="font-mono-num text-xs text-[var(--text-muted)]">{formatMoney(item.unit_price_paise)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <AmountField line={line} standard={standard} entry={entry} onChange={onChange} suggested={itemsTotal > 0 ? itemsTotal : null} testId={testId} />
      <ReasonAndNote entry={entry} onChange={onChange} reasons={reasons} testId={testId} />
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" className="accent-[var(--accent)]" checked={Boolean(entry.save)} onChange={(e) => onChange({ ...entry, save: e.target.checked })} data-testid={`${testId}-save`} />
        {t("customFee.saveToPlan")}
      </label>
    </div>
  );
}
